/**
 * "Show me" (W2-14 onboarding-wizard; DESIGN.md §5.14; contract HelpTopicDef.showMe): finds the part of the editor a
 * help topic or a tour step talks about, bringing it on screen first (the right workspace, the panel's dock tab or
 * drawer) and waiting briefly for it to appear.
 *
 * Targets: `helpId` (an element marked `data-help-id`) or `panel` (a registered panel id, revealed with
 * `ui.showPanel`, or a slot id such as 'timing.left', found by `data-slot`). The shell marks its well-known controls
 * (`transport.play`, `problems.chip`, `workspace.<id>`); some help ids also fall back to the panels' slots. Below
 * 1366 px the shell moves the status chips into its More menu; the problems chip then falls back to that menu's
 * button (`isCollapsedChip` tells the two apart).
 */

import type { AppServices, PanelSlot } from '../../app/registry/types';
import type { WorkspaceId } from '../../state/types';

export interface HelpTarget {
  readonly panel?: string;
  readonly helpId?: string;
}

/** Selectors tried after `[data-help-id="<id>"]` for well-known ids. */
const FALLBACKS: Readonly<Record<string, readonly string[]>> = {
  'preview-stage': ['[data-help-id="preview-panel"]', '[data-slot="chart.preview"]'],
  'preview-panel': ['[data-slot="chart.preview"]'],
  'tool-rail': ['[data-slot="chart.tools"]'],
  timeline: ['[data-slot="chart.timeline"]'],
  'motion-columns': ['[data-help-id="curve-editor"]', '[data-help-id="timeline"]', '[data-slot="chart.timeline"]'],
  'lines.panel': ['[data-region="lines"]', '[data-slot="chart.dock"]'],
};

/** Stand-ins found by their accessible name (message keys of the shell's catalogue). */
const LABELLED: Readonly<Record<string, readonly string[]>> = {
  // Status chips collapsed into the top bar's More menu (narrow windows).
  'problems.chip': ['shell.overflowAttention', 'shell.overflow'],
};

/** Is `el` the stand-in for a collapsed problems chip (the More menu button) rather than the chip itself? */
export function isCollapsedChip(el: Element): boolean {
  return el.getAttribute('data-help-id') !== 'problems.chip';
}

/** Workspace a help id or slot lives in (null: any workspace, e.g. the top bar). */
function workspaceOf(target: HelpTarget): WorkspaceId | null {
  const key = target.helpId ?? target.panel ?? '';
  if (key.startsWith('timing.') || key.startsWith('timing-')) return 'timing';
  if (key.startsWith('publish.')) return 'publish';
  if (key.startsWith('workspace.') || key === 'problems.chip' || key.startsWith('transport.')) return null;
  if (key.startsWith('chart.') || target.helpId !== undefined) return 'chart';
  return null;
}

function isVisible(el: Element): boolean {
  if (!el.isConnected) return false;
  const r = el.getBoundingClientRect();
  // jsdom lays nothing out: a connected element counts as visible there.
  return r.width > 0 || r.height > 0 || (r.top === 0 && r.left === 0 && r.right === 0 && r.bottom === 0);
}

function query(root: ParentNode, selector: string): Element | null {
  try {
    for (const el of root.querySelectorAll(selector)) if (isVisible(el)) return el;
  } catch {
    // An invalid selector (odd help id): nothing found.
  }
  return null;
}

function cssString(s: string): string {
  return s.replace(/["\\]/g, '\\$&');
}

/** The element a target points at, if it is on screen now. */
export function findHelpTarget(
  services: Pick<AppServices, 'registry' | 'i18n'>,
  target: HelpTarget,
  root: ParentNode = document,
): Element | null {
  if (target.helpId !== undefined) {
    const direct = query(root, `[data-help-id="${cssString(target.helpId)}"]`);
    if (direct !== null) return direct;
    for (const sel of FALLBACKS[target.helpId] ?? []) {
      const el = query(root, sel);
      if (el !== null) return el;
    }
    for (const key of LABELLED[target.helpId] ?? []) {
      const el = query(root, `button[aria-label="${cssString(services.i18n.t(key))}"]`);
      if (el !== null) return el;
    }
  }
  if (target.panel !== undefined) {
    const slot = slotOfPanel(services, target.panel) ?? target.panel;
    const el =
      query(root, `[data-slot="${cssString(slot)}"]`) ?? query(root, `[data-help-id="${cssString(target.panel)}"]`);
    if (el !== null) return el;
  }
  return null;
}

const SLOTS: readonly PanelSlot[] = [
  'chart.tools',
  'chart.timeline',
  'chart.preview',
  'chart.dock',
  'chart.inspector',
  'timing.left',
  'timing.main',
  'timing.bottom',
  'publish.left',
  'publish.right',
  'topbar.left',
  'topbar.center',
  'topbar.right',
  'statusbar',
];

/** Slot of a registered panel id (null when no module registers a panel with that id). */
export function slotOfPanel(services: Pick<AppServices, 'registry'>, id: string): PanelSlot | null {
  for (const slot of SLOTS) if (services.registry.panels(slot).some((p) => p.id === id)) return slot;
  return null;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Brings a target on screen and returns its element (null when it does not appear within `timeoutMs`): switches to
 * its workspace when a chart is open, reveals a registered panel, then waits for the element.
 */
export async function revealHelpTarget(
  services: Pick<AppServices, 'registry' | 'store' | 'editor' | 'ui' | 'i18n'>,
  target: HelpTarget,
  opts: { readonly timeoutMs?: number; readonly root?: ParentNode } = {},
): Promise<Element | null> {
  const root = opts.root ?? document;
  const now = findHelpTarget(services, target, root);
  if (now !== null) return now;
  const ws = workspaceOf(target);
  if (services.store !== null && ws !== null && services.editor.getState().workspace !== ws) {
    await services.registry.run('app.workspace', { workspace: ws }, 'api');
  }
  if (target.panel !== undefined && slotOfPanel(services, target.panel) !== null) services.ui.showPanel(target.panel);
  const deadline = Date.now() + (opts.timeoutMs ?? 1500);
  for (;;) {
    const el = findHelpTarget(services, target, root);
    if (el !== null || Date.now() >= deadline) return el;
    await sleep(50);
  }
}
